import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faSave, faTimes } from "@fortawesome/free-solid-svg-icons";
import { Button } from "../ui";

interface DmNotesFieldProps {
  /** Current DM notes value */
  value?: string;
  /** Called when the user saves edited DM notes */
  onSave?: (_dmNotes: string) => Promise<void>;
  /** Additional CSS classes for the container */
  className?: string;
}

/**
 * DmNotesField renders DM-private notes in markdown with an inline edit toggle.
 * Always visible — shows a placeholder when empty.
 */
const DmNotesField: React.FC<DmNotesFieldProps> = ({ value = "", onSave, className = "" }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);

  const handleEdit = () => {
    setDraft(value);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setDraft(value);
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      await onSave(draft);
      setIsEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`p-3 bg-amber-900/10 border border-amber-800/30 rounded ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <span className="text-amber-500/70 text-xs tracking-widest uppercase">🔒 DM Notes</span>
        {onSave && !isEditing && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleEdit}
            className="text-amber-500/60 hover:text-amber-400 p-1"
            title="Edit DM Notes"
          >
            <FontAwesomeIcon icon={faEdit} />
          </Button>
        )}
        {onSave && isEditing && (
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="text-green-400 hover:text-green-300 p-1"
              title="Save"
            >
              <FontAwesomeIcon icon={faSave} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              disabled={saving}
              className="text-zinc-400 hover:text-zinc-300 p-1"
              title="Cancel"
            >
              <FontAwesomeIcon icon={faTimes} />
            </Button>
          </div>
        )}
      </div>

      {/* Content */}
      {isEditing ? (
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={4}
          placeholder="Private notes visible only to you…"
          className="w-full bg-amber-900/10 border border-amber-800/30 rounded px-3 py-2 text-sm text-amber-200/80 placeholder-amber-700/50 focus:outline-none focus:border-amber-600/50 resize-y"
        />
      ) : value ? (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            p: ({ children, ...props }) => (
              <p {...props} className="text-amber-200/70 text-sm my-0.5">
                {children}
              </p>
            ),
            strong: ({ children, ...props }) => (
              <strong {...props} className="text-amber-100/80 font-semibold">
                {children}
              </strong>
            ),
            em: ({ children, ...props }) => (
              <em {...props} className="text-amber-200/60 italic">
                {children}
              </em>
            ),
            ul: ({ children, ...props }) => (
              <ul {...props} className="list-disc list-inside my-1 ml-2 text-amber-200/70 text-sm">
                {children}
              </ul>
            ),
            ol: ({ children, ...props }) => (
              <ol
                {...props}
                className="list-decimal list-inside my-1 ml-2 text-amber-200/70 text-sm"
              >
                {children}
              </ol>
            ),
            li: ({ children, ...props }) => (
              <li {...props} className="text-amber-200/70 text-sm my-0.5">
                {children}
              </li>
            ),
          }}
        >
          {value}
        </ReactMarkdown>
      ) : (
        <p className="text-amber-700/50 text-sm italic">No DM notes yet.</p>
      )}
    </div>
  );
};

export default DmNotesField;
